import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';

interface RouterDNIEditProps {
  onSubmit: () => void;
  cpeId: string;
  scanGap: number;
  overrideEmail: string;
  notificationMutedUntil?: string | null;
  // Current per-CPE WAN link-flap alert setting. true/undefined (legacy rows)
  // means customers are alerted; false means muted to customers.
  wanFlapAlertEnabled?: boolean;
}

// The muted-until timestamp comes from the DB as a naive UTC value; treat a
// missing timezone as UTC so the comparison and display are correct.
const parseMutedUntil = (value?: string | null): Date | null => {
  if (!value) return null;
  const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value)
    ? value
    : `${value.replace(' ', 'T')}Z`;
  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
};

export const RouterDNIEdit = ({ onSubmit, cpeId, scanGap, overrideEmail, notificationMutedUntil, wanFlapAlertEnabled }: RouterDNIEditProps) => {
  const api = useApi();

  const mutedUntilDate = parseMutedUntil(notificationMutedUntil);
  const currentlyMuted = mutedUntilDate !== null && mutedUntilDate.getTime() > Date.now();
  // Checked == muted to customers. Only an explicit false is muted; undefined
  // (legacy rows predating the column) reads as alerting, matching the backend.
  const flapCurrentlyMuted = wanFlapAlertEnabled === false;
  // Remaining whole hours on the current mute, shown as the default duration so
  // the field reflects the live configuration (like the override email does).
  const initialMuteHours = currentlyMuted && mutedUntilDate
    ? Math.max(1, Math.ceil((mutedUntilDate.getTime() - Date.now()) / 3_600_000)).toString()
    : '24';

  const [dni, setDNI] = useState<string>('1');
  const [email, setEmail] = useState<string>('');
  const [overrideChecked, setOverrideChecked] = useState<boolean>(false);
  const [muteChecked, setMuteChecked] = useState<boolean>(false);
  const [muteHours, setMuteHours] = useState<string>('24');
  const [flapMuteChecked, setFlapMuteChecked] = useState<boolean>(false);

  useEffect(() => {
    setDNI(scanGap.toString())
    setEmail(overrideEmail.toString())
    setOverrideChecked(overrideEmail.length > 0)
    setMuteChecked(currentlyMuted)
    setMuteHours(initialMuteHours)
    setFlapMuteChecked(flapCurrentlyMuted)
  }, []);

  const toggleFlapAlert = useMutation(api.toggleCpeWanFlapAlertApiV1ToggleCpeWanFlapAlertPost, {
    onSuccess() {
      toast.success('Updated WAN flapping notification successfully.');
    },
    onError: apiError,
  });

  const updateDNI = useMutation(api.setCpeScanIntervalApiV1SetCpeScanIntervalPost, {
    onSuccess() {
      toast.success('Updated down notification interval successfully.');
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    // Mirror the override behaviour: leave mute untouched unless the user
    // actually changed it. mute_notification is tri-state on the backend -
    // undefined preserves the existing window, so just editing the interval
    // never disturbs (or resets) an active mute.
    let mute_notification: boolean | undefined;
    let mute_hours: number | undefined;
    if (muteChecked !== currentlyMuted) {
      mute_notification = muteChecked;
      mute_hours = muteChecked ? parseInt(muteHours) : undefined;
    } else if (muteChecked && parseInt(muteHours) !== parseInt(initialMuteHours)) {
      // Still muted, but the user changed the duration - re-arm from now.
      mute_notification = true;
      mute_hours = parseInt(muteHours);
    }

    // WAN flapping notification is a separate persistent flag on its own
    // endpoint; only write it when the user actually changed it. enabled is the
    // inverse of the mute checkbox (checked = muted to customers).
    if (flapMuteChecked !== flapCurrentlyMuted) {
      toggleFlapAlert.mutate({ cpe_id: cpeId, enabled: !flapMuteChecked });
    }

    updateDNI.mutate({
      cpe_id: cpeId,
      scan_interval: parseInt(dni),
      override_email: overrideChecked ? email : '',
      mute_notification,
      mute_hours,
    });
  };

  return (
    <Box pt={2}>
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Down Notification Interval (in units of one minute)"
        variant="outlined"
        value={dni}
        onChange={(e) => setDNI(e.target.value)}
        autoFocus
      />
      <FormControlLabel
        // display:flex (vs MUI's default inline-flex) makes each checkbox a
        // block so the three always stack vertically, left-aligned, instead of
        // flowing onto one line when their conditional fields are hidden.
        sx={{ mb: 4, display: 'flex' }}
        control={
          <Checkbox
            checked={overrideChecked}
            onChange={(e) => setOverrideChecked(e.target.checked)}
          />
        }
        label="Enable Event Alert Override(Optional)"
      />
      {overrideChecked && <>
        <TextField
          sx={{ mb: 4 }}
          fullWidth
          label="Event Override Email (Optional)"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
        />
      </>}
      <FormControlLabel
        sx={{ mb: muteChecked ? 2 : 4, display: 'flex' }}
        control={
          <Checkbox
            checked={muteChecked}
            onChange={(e) => setMuteChecked(e.target.checked)}
          />
        }
        label="Mute Link Alerts"
      />
      {muteChecked && <>
        <TextField
          sx={{ mb: 2 }}
          fullWidth
          type="number"
          label="Mute Duration (hours from now, 1-168)"
          variant="outlined"
          value={muteHours}
          onChange={(e) => setMuteHours(e.target.value)}
          inputProps={{ min: 1, max: 168 }}
        />
        {currentlyMuted && mutedUntilDate && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Currently muted until {mutedUntilDate.toLocaleString()}. Leave the
            duration unchanged to keep this window; change it to re-mute from now.
          </Typography>
        )}
      </>}
      <FormControlLabel
        sx={{ mb: 4, display: 'flex' }}
        control={
          <Checkbox
            checked={flapMuteChecked}
            onChange={(e) => setFlapMuteChecked(e.target.checked)}
          />
        }
        label="Mute Brown Circuit Alerts"
      />
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit}>
          Save
        </Button>
      </Box>
    </Box>
  );
};
